import { Box, Checkbox, FormControlLabel, Typography } from '@material-ui/core'
import { RadioButtonChecked, RadioButtonUnchecked } from '@material-ui/icons'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ActionButton,
  AgreementContent,
  StyledCard,
  StyledCardContent,
  StyledDivider,
} from 'src/components/clubPrivacyAgreement/common'
import Toast, { ToastTypeEnum } from 'src/components/feedback/Toast'
import FullScreenBackdropDialog from 'src/components/layout/FullScreenBackdropDialog'
import { AgreementDocumentResult } from 'src/generated/graphql'
import useToast from 'src/hooks/useToast'
import styled, { css } from 'styled-components'
import ModalLink from 'src/components/navigation/ModalLink'
import { ModalPaths } from 'src/routes'

const StyledCheckbox = styled(Checkbox)`
  svg {
    width: 27px;
    height: 27px;
  }
`

const ScrollContainer = styled.div(
  ({ theme }) => css`
    flex: 1;
    overflow-y: auto;
    padding-right: ${theme.spacing(3)}px;
  `
)

const CardContent = styled(StyledCardContent)(
  ({ theme }) => css`
    max-height: 100%;
    padding-right: ${theme.spacing(2)}px;
  `
)

export type ClubTermsProps = {
  document: Pick<AgreementDocumentResult, 'content'>
  signAgreement: () => void
  loading: boolean
  error: boolean
}

const ClubPrivacyAgreement = ({
  document,
  signAgreement,
  error,
  loading,
}: ClubTermsProps) => {
  const { t } = useTranslation()

  const [isAccepted, setIsAccepted] = useState(false)
  const toggleAccepted = () => setIsAccepted(val => !val)

  const { toastState, isToastOpen, showToast, hideToast } = useToast()

  useEffect(() => {
    if (error) {
      showToast({
        variant: ToastTypeEnum.ERROR,
        message: t('ClubPrivacyAgreement.acceptErrorToast'),
      })
    } else {
      hideToast()
    }
  }, [error]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FullScreenBackdropDialog>
      <StyledCard raised>
        <CardContent>
          <Typography variant="h6">
            {t('ClubPrivacyAgreement.title')}
          </Typography>
          <StyledDivider />
          <ScrollContainer>
            <AgreementContent>
              <Typography variant="body1">{document.content}</Typography>
            </AgreementContent>
            <FormControlLabel
              label={<strong>{t('ClubPrivacyAgreement.agreeText')}</strong>}
              control={
                <StyledCheckbox
                  checked={isAccepted}
                  onChange={toggleAccepted}
                  icon={<RadioButtonUnchecked />}
                  checkedIcon={<RadioButtonChecked />}
                />
              }
            />
            <Box
              mt={8}
              mb={2}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <ModalLink to={ModalPaths.SwitchProfile}>
                <ActionButton
                  variant="contained"
                  color="secondary"
                  loading={loading}
                >
                  {t('Global.buttonLabels.cancel')}
                </ActionButton>
              </ModalLink>
              <ActionButton
                variant="contained"
                color="primary"
                disabled={!isAccepted}
                onClick={signAgreement}
                loading={loading}
              >
                {t('ClubPrivacyAgreement.nextLabel')}
              </ActionButton>
            </Box>
          </ScrollContainer>
          <Toast
            isOpen={isToastOpen}
            handleClose={hideToast}
            message={toastState?.message}
            variant={toastState?.variant}
          />
        </CardContent>
      </StyledCard>
    </FullScreenBackdropDialog>
  )
}

export default ClubPrivacyAgreement
